import { supabase } from '@/lib/supabase';
import type { OrgUser, AdminSessionMode } from '@/types';

export const orgUsersApi = {
  /** List users that belong to a given organization (joined with users table). */
  list: async (orgId: string): Promise<OrgUser[]> => {
    const { data: rows, error } = await supabase
      .from('org_users')
      .select('user_id, role')
      .eq('organization_id', orgId);
    if (error) throw error;
    if (!rows?.length) return [];

    const ids = rows.map((r: { user_id: string }) => r.user_id);
    const { data: users, error: usersErr } = await supabase
      .from('users')
      .select('id, email, first_name, last_name, avatar_url, phone')
      .in('id', ids);
    if (usersErr) throw usersErr;

    const roleById = new Map(
      rows.map((r: { user_id: string; role: string }) => [r.user_id, r.role]),
    );

    return (users ?? []).map((u: {
      id: string;
      email: string;
      first_name: string | null;
      last_name: string | null;
      avatar_url: string | null;
      phone: string | null;
    }) => {
      const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
      return {
        user_id: u.id,
        email: u.email,
        full_name: full || null,
        role: roleById.get(u.id) ?? '—',
        avatar_url: u.avatar_url,
        phone: u.phone,
      };
    });
  },

  /**
   * Generate a single-use magic-link so the admin can log into hub.jumpstart.co.il
   * as the given user. The URL must be opened in a new window/tab.
   */
  impersonate: async (
    orgId: string,
    targetUserId: string,
  ): Promise<{ url: string }> => {
    const { data, error } = await supabase.functions.invoke('admin-impersonate-user', {
      body: { org_id: orgId, target_user_id: targetUserId },
    });
    if (error) throw error;
    if (!data?.url) throw new Error('No URL returned from impersonation function');
    return data as { url: string };
  },

  impersonateAsOwner: async (
    orgId: string,
    mode: AdminSessionMode,
    reason?: string,
  ): Promise<{ url: string }> => {
    const { data, error } = await supabase.functions.invoke('admin-impersonate-owner', {
      body: { org_id: orgId, mode, reason },
    });
    if (error) throw error;
    if (!data?.url) throw new Error('No URL returned from owner impersonation function');
    return data as { url: string };
  },

  resetOwnerPassword: async (orgId: string): Promise<{ email: string; email_sent: boolean }> => {
    const { data, error } = await supabase.functions.invoke('admin-reset-owner-password', {
      body: { org_id: orgId },
    });
    if (error) throw error;
    if (!data?.email) throw new Error('No owner email returned from reset function');
    return data as { email: string; email_sent: boolean };
  },

  invite: async (args: {
    organization_id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    role_slug: 'admin' | 'manager' | 'marketing_manager' | 'staff' | 'viewer';
    reason: string;
  }): Promise<{ ok: true; target_user_id: string }> => {
    const { data, error } = await supabase.functions.invoke('admin-user-access', {
      body: { action: 'invite_user', ...args },
    });
    if (error) throw error;
    if ((data as { error?: string } | null)?.error) throw new Error((data as { error: string }).error);
    return data as { ok: true; target_user_id: string };
  },
};
